import React from "react";
import { motion } from "framer-motion";
import PicImg from "../PicImg";

/* =====================================================================
   LEADERSHIP & LEGACY — premium split layout
   Left  : large founder portrait (currently elegant placeholder card;
           swap the <FounderPortrait /> contents with an <PicImg loading="lazy" decoding="async" /> later)
   Right : eyebrow + heading + narrative + founder name card + quote
   ===================================================================== */

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: "easeOut" } },
};

function FounderPortrait() {
  return (
    <div className="relative aspect-[4/5] w-full overflow-hidden rounded-2xl shadow-[0_30px_70px_-25px_rgba(0,0,0,0.5)] bg-gradient-to-br from-[#1a1a1a] via-[#0f0f0f] to-[#000]">
      <PicImg
        src="/founder-jayantibhai.png"
        alt="Mr. Jayantibhai Tarabdamali, Founder — LIFTILE"
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
      />
    </div>
  );
}

export default function LeadershipSection() {
  return (
    <section
      data-testid="about-leadership"
      className="relative bg-white py-24 md:py-32 lg:py-40 overflow-hidden"
    >
      {/* Soft orange ambient accent */}
      <div
        aria-hidden
        className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full pointer-events-none opacity-[0.10]"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.7) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(40px)",
        }}
      />

      <div className="container-x relative">
        <div className="grid grid-cols-1 lg:grid-cols-[42fr_58fr] gap-12 lg:gap-20 items-start">
          {/* -------------------- LEFT — Portrait -------------------- */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative lg:sticky lg:top-28"
          >
            {/* Decorative orange accents around portrait */}
            <div
              aria-hidden
              className="absolute -bottom-6 -right-6 w-28 h-28 md:w-40 md:h-40 bg-liftile-orange rounded-md hidden md:block"
            />
            <div
              aria-hidden
              className="absolute -top-5 -left-5 w-16 h-16 border-2 border-liftile-orange rounded-md hidden md:block"
            />
            <div className="relative">
              <FounderPortrait />
              {/* Orange accent line under portrait */}
              <div className="flex items-center gap-3 mt-5 text-liftile-matte-black/65">
                <span className="block w-8 h-px bg-liftile-orange" />
                <span className="font-heading text-[10px] uppercase tracking-[0.28em]">
                  Founder · Since 1998
                </span>
              </div>
            </div>
          </motion.div>

          {/* -------------------- RIGHT — Narrative -------------------- */}
          <div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeUp}
            >
              <div className="heading-eyebrow mb-4 text-liftile-orange">
                Our Leadership
              </div>
              <h2
                data-testid="about-leadership-heading"
                className="font-display uppercase text-liftile-matte-black leading-[0.95] tracking-tight text-4xl sm:text-5xl md:text-6xl"
              >
                Built on Experience.
                <br />
                <span className="text-liftile-orange">Driven by Vision.</span>
              </h2>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeUp}
              transition={{ delay: 0.1 }}
              className="mt-9 space-y-5 font-body text-liftile-matte-black/75 text-base md:text-lg leading-relaxed max-w-2xl"
            >
              <p>
                LIFTILE is built upon more than 26 years of practical experience
                in construction machinery manufacturing — a foundation rooted in
                precision workmanship, dependable engineering and the long-term
                trust of every customer we serve.
              </p>
              <p>
                Today, we combine that traditional manufacturing expertise with
                modern engineering, premium quality standards and customer-focused
                innovation — building machines engineered for long service life,
                dependable performance and maximum safety on every site.
              </p>
            </motion.div>

            {/* Founder name card */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: 0.15, duration: 0.65, ease: "easeOut" }}
              data-testid="about-founder-card"
              className="mt-12 max-w-2xl bg-liftile-cream rounded-xl border border-black/[0.05] p-7 md:p-8 relative overflow-hidden"
            >
              <div
                aria-hidden
                className="absolute top-0 left-0 bottom-0 w-1 bg-liftile-orange"
              />
              <div className="pl-3">
                <h3 className="font-display uppercase text-xl md:text-2xl text-liftile-matte-black tracking-tight leading-none">
                  Jayantibhai Tarabdamali
                </h3>
                <div className="mt-2 font-heading text-[11px] md:text-xs uppercase tracking-[0.28em] text-liftile-orange">
                  Founder &amp; Manufacturing Mentor
                </div>
                <p className="mt-5 font-body text-sm md:text-base text-liftile-matte-black/75 leading-relaxed">
                  With more than 26 years of practical experience in construction
                  machinery manufacturing, fabrication and engineering, his
                  expertise continues to shape the quality standards behind every
                  LIFTILE machine.
                </p>
              </div>
            </motion.div>

            {/* Premium quote */}
            <motion.figure
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: 0.2, duration: 0.7, ease: "easeOut" }}
              data-testid="about-leadership-quote"
              className="mt-12 md:mt-14 max-w-2xl relative pl-6 md:pl-8"
            >
              <span
                aria-hidden
                className="absolute left-0 top-1 bottom-1 w-[3px] bg-liftile-orange rounded"
              />
              <blockquote className="font-display italic text-xl md:text-2xl lg:text-[1.7rem] text-liftile-matte-black leading-snug tracking-tight">
                &ldquo;Experience builds the foundation.
                <br />
                Innovation builds the future.&rdquo;
              </blockquote>
            </motion.figure>
          </div>
        </div>
      </div>
    </section>
  );
}
